import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DocumentTypeService } from '../../../_services/document-type.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'acabim-admin-document-usage-type-management',
  templateUrl: './document-usage-type-management.component.html',
  styleUrls: ['./document-usage-type-management.component.scss']
})
export class DocumentUsageTypeManagementComponent implements OnInit {
  manageDocumentsForm: FormGroup | undefined = undefined;
  editDocumentUsageForm: FormGroup | undefined = undefined;
  initialEditFormValue: {[key: string]: number | string} = {};

  documentUsageTypes = [];
  documentStatusTypes = [{name: 'Active', key: 1}, {name: 'In-active', key: 0}];
  selectedUsageType: {id: number, name: string, status: number} | null = null;

  isManageDocumentFormReady = true;
  isEditModalReady = false;
  isEditFormValid = true;
  hasEditFormChanged = false;

  manageDocumentsFormMessage = `These are document usage types that you can use to group your documents. Choose an existing type
                                and click Edit to change its name, or enter a new one to add to the list. Archive
                                any document usage type that is not in use by setting its status to 'inactive'.`;

  constructor(private readonly documentTypeService: DocumentTypeService, private readonly messageService: MessageService) {}

  ngOnInit(): void {
    this.initializeForms();
    this.documentTypeService.getDocumentUsageTypes().subscribe(data => {
      this.documentUsageTypes = data.data;
    });
    this.editDocumentUsageForm?.statusChanges.subscribe(status => {
      this.isEditFormValid = status === 'VALID' ? true : false;
    });
    this.editDocumentUsageForm?.valueChanges.subscribe(_ => {
      // Check if form inputs have been changed by user
      this.hasEditFormChanged = Object.keys(this.initialEditFormValue).some(key => this.editDocumentUsageForm?.value[key] !== this.initialEditFormValue[key]);
    });
  }

  private initializeForms(): void {
    this.manageDocumentsForm = new FormGroup({
      currentDocumentUsageTypes: new FormControl(null),
      newDocumentUsageType: new FormControl(null, [Validators.pattern('^[A-Za-z0-9-_ ]+$')])
    });
    this.editDocumentUsageForm = new FormGroup({
      documentUsageTypeName: new FormControl(null, [Validators.required, Validators.pattern('^[A-Za-z0-9-_ ]+$')]),
      documentUsageTypeStatus: new FormControl(null)
    });
  }

  // Check if usage type name is empty or contains only spaces
  private isUsageTypeNameValid(usageTypeName: string): boolean {
    if (usageTypeName) {
      return usageTypeName.trim().length !== 0;
    }
    return false;
  }

  addDocumentUsageTypeHandler(): void {
    if (this.manageDocumentsForm) {
      const newUsageTypeName = this.manageDocumentsForm.controls.newDocumentUsageType.value;
      if (!this.isUsageTypeNameValid(newUsageTypeName)) {
          this.messageService.add({severity: 'error', summary: 'Please enter a valid usage type'});
      } else {
        if (this.manageDocumentsForm.controls.newDocumentUsageType.valid) {
          this.isManageDocumentFormReady = false;
          this.addDocumentUsageType(newUsageTypeName);
        }
      }
      this.manageDocumentsForm?.controls.newDocumentUsageType.patchValue(null);
    }
  }

  addDocumentUsageType(newUsageTypeName: string): void {
    this.isManageDocumentFormReady = false;
    this.documentTypeService.addDocumentUsageType(newUsageTypeName).subscribe(_ => {
      this.messageService.add({severity: 'success', summary: 'Usage type added'});
      this.refreshDocumentUsageTypes();
      this.isManageDocumentFormReady = true;
    }, () => {
      this.messageService.add({severity: 'error', summary: 'This usage type already exists'});
      this.isManageDocumentFormReady = true;
    });
  }

  selectDocumentUsageTypeHandler(event: {value: {id: number, name: string, status: number}[]}): void {
    if (event.value.length !== 0) {
      this.selectedUsageType = event.value[0];
    } else {
      this.selectedUsageType = null;
    }
  }

  editDocumentUsageTypeHandler(): void {
    if (this.selectedUsageType) {
      this.editDocumentUsageForm?.get('documentUsageTypeName')?.patchValue(this.selectedUsageType.name);
      this.editDocumentUsageForm?.get('documentUsageTypeStatus')?.patchValue(this.selectedUsageType.status);
      this.isEditModalReady = true;
    }
    this.initialEditFormValue = this.editDocumentUsageForm?.value;
  }

  toggleEditModal(event: boolean): void {
    this.isEditModalReady = event;
    if (!event) {
    }
  }

  clearUsageTypesMultiselect(): void {
    this.manageDocumentsForm?.controls.currentDocumentUsageTypes.patchValue(null);
  }

  refreshDocumentUsageTypes(): void {
     this.documentTypeService.getDocumentUsageTypes().subscribe(data => {
      this.documentUsageTypes = data.data;
    });
  }

  updateDocumentUsageTypeName(newUsageTypeName: string): void {
     this.documentTypeService.updateDocumentUsageType(String(this.selectedUsageType?.id), newUsageTypeName).subscribe(_ => {
        this.messageService.add({severity: 'success', summary: 'Usage type updated'});
        this.prepareManageDocumentForm();
      });
  }

  updateDocumentUsageTypeStatus(): void {
    if (this.selectedUsageType) {
      this.documentTypeService.toggleDocumentUsageTypeStatus(this.selectedUsageType.id).subscribe(_ => {
        this.messageService.add({severity: 'success', summary: 'Usage type updated'});
        this.prepareManageDocumentForm();
      });
    }
  }

  updateDocumentUsageType(newUsageTypeName: string): void {
    if (this.selectedUsageType) {
      forkJoin({
                updateName : this.documentTypeService.updateDocumentUsageType(String(this.selectedUsageType?.id), newUsageTypeName),
                updateStatus: this.documentTypeService.toggleDocumentUsageTypeStatus(this.selectedUsageType.id)
               }).subscribe(_ => {
                  this.messageService.add({severity: 'success', summary: 'Usage type updated'});
                  this.prepareManageDocumentForm();
        });
    }
  }

  prepareManageDocumentForm(): void{
    this.refreshDocumentUsageTypes();
    this.clearUsageTypesMultiselect();
    this.isManageDocumentFormReady = true;
  }

  submitEditFormHandler(): void {
    if (this.isEditFormValid) {
      if (this.selectedUsageType && this.hasEditFormChanged) {
        const currentUsageTypeName = this.selectedUsageType?.name;
        const currentUsageTypeStatus = this.selectedUsageType?.status;
        const newUsageTypeName = this.editDocumentUsageForm?.controls.documentUsageTypeName.value;
        const newUsageTypeStatus = +this.editDocumentUsageForm?.controls.documentUsageTypeStatus.value;

        this.isManageDocumentFormReady = false;

        if ((newUsageTypeName !== currentUsageTypeName) && (newUsageTypeStatus === currentUsageTypeStatus)) {
          this.updateDocumentUsageTypeName(newUsageTypeName);
        }
        if ((newUsageTypeName === currentUsageTypeName) && (newUsageTypeStatus !== currentUsageTypeStatus)) {
          this.updateDocumentUsageTypeStatus();
        }
        if ((newUsageTypeName !== currentUsageTypeName) && (newUsageTypeStatus !== currentUsageTypeStatus)) {
          this.updateDocumentUsageType(newUsageTypeName);
        }
      }
      this.isEditModalReady = false;
    }
  }
}
